import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { formatDate, formatDateTime } from '../utils/dateUtils';

const ClientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchClientDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchClientDetails = async () => {
    try {
      const response = await api.get(`/admin/customers/${id}`);
      console.log('Client details response:', response.data);
      setClient(response.data.customer || response.data);
    } catch (err) {
      console.error('Fetch client details error:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to fetch customer details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Loading customer details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <button
          onClick={() => navigate('/clients')}
          className="mb-4 text-primary hover:text-opacity-80"
        >
          &larr; Back to Clients
        </button>
        <div className="bg-red-50 text-red-600 p-4 rounded-md">
          {error}
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div>
        <button
          onClick={() => navigate('/clients')}
          className="mb-4 text-primary hover:text-opacity-80"
        >
          &larr; Back to Clients
        </button>
        <div className="text-gray-600">Client not found</div>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => navigate('/clients')}
        className="mb-4 text-primary hover:text-opacity-80 font-medium"
      >
        &larr; Back to Clients
      </button>

      <h1 className="text-3xl font-bold text-primary mb-6">Client Details</h1>

      {/* Client Info */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center mb-6">
          <div className="flex-shrink-0 h-16 w-16 bg-primary text-white rounded-full flex items-center justify-center font-bold text-2xl">
            {client.name?.charAt(0).toUpperCase()}
          </div>
          <div className="ml-6">
            <h2 className="text-2xl font-semibold text-gray-900">{client.name}</h2>
            <p className="text-gray-600">{client.email}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600">Phone Number</p>
            <p className="text-lg font-medium text-gray-900">{client.phone}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Total Appointments</p>
            <p className="text-lg font-medium text-gray-900">
              {client.appointments?.length || 0}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Member Since</p>
            <p className="text-lg font-medium text-gray-900">
              {client.createdAt
                ? formatDateTime(client.createdAt)
                : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Appointments History */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          Appointment History
        </h3>

        {client.appointments && client.appointments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {client.appointments.map((appointment) => (
                  <tr key={appointment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(appointment.date)}
                      </div>
                      <div className="text-sm text-gray-500">{appointment.time}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {appointment.duration} min
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${appointment.price?.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          appointment.status === 'confirmed'
                            ? 'bg-green-100 text-green-800'
                            : appointment.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : appointment.status === 'completed'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {appointment.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">
            No appointments found for this client.
          </p>
        )}
      </div>
    </div>
  );
};

export default ClientDetails;
